import { fixedUi } from '@/i18n/fixedUi';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import {
  SHEIN_ACTIVE_SHOP_STORAGE_KEY,
  isSheinShopAuthorized,
  shopTypeLabelForSlug,
} from '@/shein-platform/constants/sheinRoutes';
import { useSheinWorkspaceAuthOptional } from '@/shein-platform/context/SheinWorkspaceAuthContext';
import {
  normalizeSheinShopType,
  shopTypeEquals,
} from '@/utils/platformShopCatalog';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
  isConcreteShopId,
} from '@/utils/platformShopScope';
import { getIpeWorkspaceMode } from '@/utils/api';
import { showToast } from '@/utils/toast';

const SheinShopContext = createContext(null);

function readStoredShopId() {
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(SHEIN_ACTIVE_SHOP_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

/** 发品等新标签可带 ?shopId=，优先于本地「全部」偏好 */
function readUrlShopId() {
  if (typeof window === 'undefined') return '';
  try {
    const id = new URLSearchParams(window.location.search).get('shopId');
    return isConcreteShopId(id) ? String(id) : '';
  } catch {
    return '';
  }
}

function persistShopId(shopId) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (shopId) {
      localStorage.setItem(SHEIN_ACTIVE_SHOP_STORAGE_KEY, String(shopId));
    } else {
      localStorage.removeItem(SHEIN_ACTIVE_SHOP_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function resolveModuleFromPath(pathname = '') {
  const seg = pathname.split('/').filter(Boolean).pop();
  return seg || 'products';
}

function isAuthRedirectError(err) {
  const code = String(err?.code || '');
  if (code === 'SHEIN_WORKSPACE_EXPIRED' || code === 'LOGIN_REQUIRED' || code === 'UNAUTHORIZED') {
    return true;
  }
  const msg = String(err?.message || '');
  return msg.includes('工作区未验证或已过期') || msg.includes('未授权，请先登录');
}

function useWorkspaceModeKey() {
  const [mode, setMode] = useState(() => getIpeWorkspaceMode());
  useEffect(() => {
    const sync = (ev) => {
      const next = ev?.detail?.mode;
      if (next === 'personal' || next === 'team') {
        setMode(next);
        return;
      }
      setMode(getIpeWorkspaceMode());
    };
    window.addEventListener('ipe:workspace-mode-changed', sync);
    window.addEventListener('ipe-workspace-mode-changed', sync);
    window.addEventListener('ipe:workspace-refresh', sync);
    return () => {
      window.removeEventListener('ipe:workspace-mode-changed', sync);
      window.removeEventListener('ipe-workspace-mode-changed', sync);
      window.removeEventListener('ipe:workspace-refresh', sync);
    };
  }, []);
  return mode;
}

export function SheinShopProvider({ children }) {
  const location = useLocation();
  const { shopType: shopTypeSlug } = useParams();
  const workspaceAuth = useSheinWorkspaceAuthOptional();
  const workspaceMode = useWorkspaceModeKey();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeShopId, setActiveShopId] = useState(() => readUrlShopId() || readStoredShopId());
  const [preferences, setPreferences] = useState(null);

  const expectedType = useMemo(() => {
    const fromSlug = shopTypeLabelForSlug(shopTypeSlug);
    if (fromSlug) return fromSlug;
    return normalizeSheinShopType(workspaceAuth?.workspaceShopType) || '';
  }, [shopTypeSlug, workspaceAuth?.workspaceShopType]);

  const scopedShops = useMemo(() => {
    const list = (shops || []).filter((s) => isSheinShopAuthorized(s));
    if (!expectedType) return list;
    return list.filter((s) => shopTypeEquals(s.shopType, expectedType, 'SHEIN'));
  }, [shops, expectedType]);

  const canUseAllShops = scopedShops.length >= 2;

  const loadShops = useCallback(async () => {
    setLoading(true);
    try {
      const [list, prefRes] = await Promise.all([
        sheinPlatformAPI.listShops(),
        sheinPlatformAPI.getPreferences().catch(() => null),
      ]);
      const sheinShops = (Array.isArray(list) ? list : []).filter(
        (s) => String(s.platformCode || '').toUpperCase() === 'SHEIN',
      );
      setShops(sheinShops);
      if (prefRes?.preferences) {
        setPreferences(prefRes.preferences);
      }
      const prefShopId = prefRes?.activeShopId ? String(prefRes.activeShopId) : '';
      const storedShopId = readStoredShopId();
      const urlShopId = readUrlShopId();
      const authorizedOfType = sheinShops.filter((s) => {
        if (!isSheinShopAuthorized(s)) return false;
        if (!expectedType) return true;
        return shopTypeEquals(s.shopType, expectedType, 'SHEIN');
      });
      setActiveShopId((prev) => {
        const candidates = [urlShopId, storedShopId, prefShopId, prev].filter(Boolean);
        for (const candidate of candidates) {
          if (isAllShopsScope(candidate)) {
            if (authorizedOfType.length >= 2) {
              persistShopId(PLATFORM_ALL_SHOPS_ID);
              return PLATFORM_ALL_SHOPS_ID;
            }
            continue;
          }
          const stored = sheinShops.find((s) => String(s.id) === String(candidate));
          if (stored && isSheinShopAuthorized(stored)) {
            if (expectedType && !shopTypeEquals(stored.shopType, expectedType, 'SHEIN')) {
              continue;
            }
            persistShopId(String(candidate));
            return String(candidate);
          }
        }
        if (!sheinShops.length && (storedShopId || prev)) {
          return String(storedShopId || prev);
        }
        persistShopId('');
        return '';
      });
    } catch (err) {
      if (isAuthRedirectError(err)) {
        return;
      }
      setShops([]);
      const msg = String(err?.message || '').trim();
      if (msg) {
        showToast(msg, 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [expectedType]);

  useEffect(() => {
    loadShops();
  }, [loadShops, workspaceMode]);

  const persistPreference = useCallback(async (shopId, extra = {}) => {
    try {
      const concrete = isConcreteShopId(shopId) ? Number(shopId) : null;
      const res = await sheinPlatformAPI.updatePreferences({
        activeShopId: concrete,
        preferences: {
          ...(preferences && typeof preferences === 'object' ? preferences : {}),
          ...extra,
          ...(isAllShopsScope(shopId) ? { allShopsMode: true } : { allShopsMode: false }),
        },
      });
      if (res?.preferences) {
        setPreferences(res.preferences);
      }
    } catch {
      /* best-effort */
    }
  }, [preferences]);

  const updatePreferences = useCallback(async (patch = {}) => {
    try {
      const nextActive = patch.activeShopId != null
        ? patch.activeShopId
        : (isConcreteShopId(activeShopId) ? Number(activeShopId) : null);
      const res = await sheinPlatformAPI.updatePreferences({
        activeShopId: nextActive,
        preferences: {
          ...(preferences && typeof preferences === 'object' ? preferences : {}),
          ...(patch.preferences && typeof patch.preferences === 'object' ? patch.preferences : {}),
        },
      });
      if (res?.preferences) {
        setPreferences(res.preferences);
      }
      return res;
    } catch (err) {
      return { ok: false, error: err };
    }
  }, [preferences, activeShopId]);

  useEffect(() => {
    if (!isConcreteShopId(activeShopId) || loading) return;
    const moduleId = resolveModuleFromPath(location.pathname);
    sheinPlatformAPI.updatePreferences({
      activeShopId: Number(activeShopId),
      preferences: { lastModule: moduleId },
    })
      .then((res) => {
        if (res?.preferences) setPreferences(res.preferences);
      })
      .catch(() => {});
  }, [location.pathname, activeShopId, loading]);

  const switchToAll = useCallback(() => {
    if (scopedShops.length < 2) {
      return { ok: false, message: '同类型已授权店铺不足 2 家，无法进入全部视图' };
    }
    if (isAllShopsScope(activeShopId)) {
      return { ok: true, unchanged: true };
    }
    setActiveShopId(PLATFORM_ALL_SHOPS_ID);
    persistShopId(PLATFORM_ALL_SHOPS_ID);
    persistPreference(PLATFORM_ALL_SHOPS_ID);
    return { ok: true, all: true };
  }, [scopedShops.length, activeShopId, persistPreference]);

  const switchShop = useCallback((shopId) => {
    if (isAllShopsScope(shopId)) {
      return switchToAll();
    }
    const shop = shops.find((s) => String(s.id) === String(shopId));
    if (!shop) {
      return { ok: false, message: '店铺不存在' };
    }
    if (!isSheinShopAuthorized(shop)) {
      return { ok: false, message: '只能切换已授权的店铺' };
    }
    const workspaceType = expectedType
      || normalizeSheinShopType(workspaceAuth?.workspaceShopType);
    const current = shops.find((s) => String(s.id) === String(activeShopId));
    const currentType = normalizeSheinShopType(current?.shopType)
      || workspaceType
      || fixedUi("半托管");
    if (!shopTypeEquals(shop.shopType, currentType, 'SHEIN')) {
      const targetType = normalizeSheinShopType(shop.shopType) || '未知';
      return {
        ok: false,
        message: `只能切换同类型店铺（当前「${currentType}」，目标「${targetType}」）。跨类型请返回门禁重进。`,
      };
    }
    const next = String(shopId);
    if (next === String(activeShopId)) {
      return { ok: true, unchanged: true, shop };
    }
    setActiveShopId(next);
    persistShopId(next);
    persistPreference(next);
    return { ok: true, shop };
  }, [shops, activeShopId, persistPreference, workspaceAuth?.workspaceShopType, expectedType, switchToAll]);

  const patchLocalShop = useCallback((shopId, patch = {}) => {
    if (!shopId || !patch || typeof patch !== 'object') return;
    setShops((prev) => prev.map((shop) => (
      String(shop.id) === String(shopId) ? { ...shop, ...patch } : shop
    )));
  }, []);

  const isAllShopsMode = isAllShopsScope(activeShopId);

  const activeShop = useMemo(
    () => (isAllShopsMode
      ? null
      : (shops.find((s) => String(s.id) === String(activeShopId)) || null)),
    [shops, activeShopId, isAllShopsMode],
  );

  const value = useMemo(
    () => ({
      shops,
      scopedShops,
      canUseAllShops,
      activeShopId,
      activeShop,
      isAllShopsMode,
      expectedShopType: expectedType,
      loading,
      preferences,
      updatePreferences,
      switchShop,
      switchToAll,
      patchLocalShop,
      reloadShops: loadShops,
    }),
    [
      shops,
      scopedShops,
      canUseAllShops,
      activeShopId,
      activeShop,
      isAllShopsMode,
      expectedType,
      loading,
      preferences,
      updatePreferences,
      switchShop,
      switchToAll,
      patchLocalShop,
      loadShops,
    ],
  );

  return (
    <SheinShopContext.Provider value={value}>
      {children}
    </SheinShopContext.Provider>
  );
}

export function useSheinShop() {
  const ctx = useContext(SheinShopContext);
  if (!ctx) {
    throw new Error('useSheinShop must be used within SheinShopProvider');
  }
  return ctx;
}

export function useSheinShopOptional() {
  return useContext(SheinShopContext);
}
